import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Check } from "lucide-react";
import { ABOUT } from "../../lib/content";
import { Eyebrow } from "../Reveal";

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.2, 1]);

  return (
    <section id="about" data-testid="about" ref={ref} className="bg-muted py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="overflow-hidden border border-border order-2 lg:order-1 w-full max-w-sm mx-auto lg:mx-0">
          <motion.img
            style={{ scale: imgScale }}
            src={ABOUT.image}
            alt="Matthew Barlow, Owner and CEO of Rest Assured Group Travel LLC"
            className="w-full h-[340px] md:h-[460px] object-cover object-top"
          />
        </div>

        <div className="order-1 lg:order-2">
          <Eyebrow>Our story</Eyebrow>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl md:text-6xl font-light tracking-tight leading-[0.95] mt-4 mb-8"
          >
            Group travel,
            <br />
            <span className="text-primary italic">handled with heart.</span>
          </motion.h2>

          <div className="space-y-5">
            {ABOUT.paragraphs.map((p, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.12 }}
                className="text-muted-foreground text-base md:text-lg font-light leading-relaxed"
              >
                {p}
              </motion.p>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4 mt-10">
            {ABOUT.values.map((v) => (
              <div key={v} className="flex items-center gap-3">
                <span className="w-7 h-7 bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                  <Check className="w-4 h-4" />
                </span>
                <span className="label-eyebrow text-foreground">{v}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-border">
            <p className="font-display text-3xl md:text-4xl text-primary italic leading-none" data-testid="about-signature">
              {ABOUT.signatureName}
            </p>
            <p className="label-eyebrow text-muted-foreground mt-2">{ABOUT.signatureTitle}</p>
          </div>
        </div>
      </div>
    </section>
  );
};
